import React, { useEffect, useState } from "react";
import { X, Download, Share, Plus } from "lucide-react";

const DISMISS_KEY = "purrflect_install_dismissed";

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isInStandaloneMode() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari never fires beforeinstallprompt — show manual guidance.
    if (isIos()) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosHelp(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setVisible(false);
      setDeferredPrompt(null);
      localStorage.setItem(DISMISS_KEY, "1");
    } else if (isIos()) {
      setShowIosHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:left-auto sm:right-4 sm:max-w-sm"
    >
      <div className="rounded-2xl border border-[#E2694A]/20 bg-white shadow-xl shadow-black/10 p-4">
        <div className="flex items-start gap-3">
          <img
            src="/pwa-icon-192.png"
            alt="Purrflect"
            className="h-11 w-11 rounded-xl shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-neutral-900">Install Purrflect</p>
            {showIosHelp ? (
              <p className="mt-1 text-xs leading-relaxed text-neutral-600">
                Tap <Share className="inline h-3.5 w-3.5 align-text-bottom" /> Share, then{" "}
                <span className="whitespace-nowrap">
                  <Plus className="inline h-3.5 w-3.5 align-text-bottom" /> Add to Home Screen
                </span>
                .
              </p>
            ) : (
              <p className="mt-1 text-xs leading-relaxed text-neutral-600">
                Add it to your home screen for one-tap access to AI vet help and reminders.
              </p>
            )}
          </div>
          <button
            data-testid="pwa-install-dismiss"
            onClick={dismiss}
            className="shrink-0 rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {!showIosHelp && (
          <button
            data-testid="pwa-install-button"
            onClick={install}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#E2694A] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#c9573b]"
          >
            <Download className="h-4 w-4" />
            {isIos() ? "How to install" : "Install app"}
          </button>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
